<template>
  <section class="cta-section" aria-labelledby="cta-title">
    <!-- Radial glow -->
    <div class="cta-glow" aria-hidden="true"></div>
    <!-- Thin top border line -->
    <div class="cta-line cta-line--top" aria-hidden="true"></div>

    <div class="cta-inner">
      <p class="cta-eyebrow">{{ $t('about.ctaEyebrow') }}</p>
      <h2 id="cta-title" class="cta-title">{{ $t('about.ctaTitle') }}</h2>
      <p class="cta-sub">{{ $t('about.ctaSub') }}</p>
      <nuxt-link :to="localePath('/contact')" class="cta-btn" :aria-label="$t('about.ctaBtn')">
        <span>{{ $t('about.ctaBtn') }}</span>
        <svg
          class="cta-arrow"
          width="20" height="20"
          viewBox="0 0 20 20"
          fill="none"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </nuxt-link>
    </div>
  </section>
</template>

<script>
export default {
  name: 'CtaSection'
}
</script>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Section ───────────────────────────────────────────────────────────
.cta-section {
  position: relative;
  padding: 180px 24px 160px;
  text-align: center;
  overflow: hidden;
  background: #fafafa;

  .theme--dark & {
    background: #09090f;
  }
}

// ── Decorative border lines ───────────────────────────────────────────
.cta-line {
  position: absolute;
  inset-inline: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba($blue, 0.22) 30%,
    rgba($purple, 0.28) 50%,
    rgba($red, 0.22) 70%,
    transparent 100%
  );

  &--top { top: 0; }
}

// ── Ambient glow ──────────────────────────────────────────────────────
.cta-glow {
  position: absolute;
  width: 700px;
  height: 700px;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle at center,
    rgba($purple, 0.09) 0%,
    rgba($blue, 0.05) 40%,
    transparent 70%
  );
  pointer-events: none;
}

// ── Content ───────────────────────────────────────────────────────────
.cta-inner {
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
}

// ── Eyebrow ───────────────────────────────────────────────────────────
.cta-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 24px;

  .theme--dark & {
    color: lighten($blue, 28%);
  }
}

// ── Title ─────────────────────────────────────────────────────────────
.cta-title {
  font-size: clamp(2.4rem, 7vw, 4.4rem);
  font-weight: 800;
  letter-spacing: -0.035em;
  line-height: 1.1;
  color: #1a1a2e;
  margin-bottom: 20px;

  .theme--dark & {
    color: #f0f0f0;
  }

  // Arabic glyphs need neutral tracking and more vertical room
  [dir='rtl'] & {
    letter-spacing: 0;
    line-height: 1.4;
  }
}

// ── Subline ───────────────────────────────────────────────────────────
.cta-sub {
  font-size: 1.05rem;
  color: #777;
  margin-bottom: 52px;
  line-height: 1.7;

  .theme--dark & {
    color: #888;
  }
}

// ── CTA Button ────────────────────────────────────────────────────────
.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 0 40px;
  height: 58px;
  border-radius: 100px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #fff !important;
  text-decoration: none;
  background: linear-gradient(130deg, $blue 0%, $purple 55%, $red 100%);
  box-shadow:
    0 8px 24px rgba($blue, 0.32),
    0 2px 4px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1),
    box-shadow 0.22s ease;
  position: relative;
  overflow: hidden;

  // Shimmer layer on hover
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(130deg, transparent 0%, rgba(255,255,255,0.14) 50%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow:
      0 16px 40px rgba($blue, 0.42),
      0 4px 8px rgba(0, 0, 0, 0.12);

    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }

  // RTL: flip gradient direction so brand colors flow naturally right-to-left
  [dir='rtl'] & {
    letter-spacing: 0;
    background: linear-gradient(130deg, $red 0%, $purple 55%, $blue 100%);
    box-shadow:
      0 8px 24px rgba($red, 0.28),
      0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.cta-arrow {
  transition: transform 0.2s ease;

  .cta-btn:hover & {
    transform: translateX(4px);
  }

  // Flip direction for RTL
  [dir='rtl'] & {
    transform: scaleX(-1);
  }

  [dir='rtl'] .cta-btn:hover & {
    transform: scaleX(-1) translateX(4px);
  }
}

// ── Responsive ────────────────────────────────────────────────────────
@media (max-width: 600px) {
  .cta-section {
    padding: 120px 20px 100px;
  }

  .cta-btn {
    height: 52px;
    padding: 0 28px;
    font-size: 0.92rem;
  }

  .cta-glow {
    width: 400px;
    height: 400px;
  }
}
</style>
